import React from 'react';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Select, DatePicker, message } from 'antd';
import {
  HealthSyncCategory,
  HealthSyncPlatform,
} from '@wellverse/shared-ts';
import dayjs from 'dayjs';
import { parseOptionalJsonField } from './json-field';

export const HealthSyncRecordCreate: React.FC = () => {
  const { formProps, saveButtonProps } = useForm({
    resource: 'health-sync-records',
  });

  return (
    <Create resource="health-sync-records" saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: Record<string, unknown>) => {
          const parsed = parseOptionalJsonField(values.payloadText);
          if (!parsed.ok) {
            message.error(parsed.error);
            return;
          }

          const payload: Record<string, unknown> = {
            userId: values.userId,
            platform: values.platform,
            category: values.category,
            dataType: values.dataType,
            externalKey: values.externalKey,
            numericValue: values.numericValue,
            unit: values.unit,
            sourceName: values.sourceName,
            startTime: dayjs(values.startTime as string).toISOString(),
            endTime: values.endTime
              ? dayjs(values.endTime as string).toISOString()
              : undefined,
            payload: parsed.value ?? undefined,
          };
          return formProps.onFinish?.(payload);
        }}
      >
        <Form.Item
          label="User ID"
          name="userId"
          rules={[
            { required: true },
            {
              pattern:
                /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
              message: 'Enter a valid UUID',
            },
          ]}
        >
          <Input placeholder="Wooki AI user UUID" />
        </Form.Item>
        <Form.Item label="Platform" name="platform" rules={[{ required: true }]}>
          <Select
            options={Object.values(HealthSyncPlatform).map((p) => ({
              label: p,
              value: p,
            }))}
          />
        </Form.Item>
        <Form.Item label="Category" name="category" rules={[{ required: true }]}>
          <Select
            options={Object.values(HealthSyncCategory).map((c) => ({
              label: c,
              value: c,
            }))}
          />
        </Form.Item>
        <Form.Item label="Data Type" name="dataType" rules={[{ required: true }]}>
          <Input placeholder="e.g. STEPS, WEIGHT, HEART_RATE" />
        </Form.Item>
        <Form.Item label="External Key" name="externalKey" rules={[{ required: true }]}>
          <Input placeholder="Unique dedup key for this user" />
        </Form.Item>
        <Form.Item label="Numeric Value" name="numericValue">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Unit" name="unit">
          <Input />
        </Form.Item>
        <Form.Item label="Source Name" name="sourceName">
          <Input />
        </Form.Item>
        <Form.Item
          label="Start Time"
          name="startTime"
          rules={[{ required: true }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="End Time" name="endTime">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Payload (JSON)" name="payloadText">
          <Input.TextArea rows={6} placeholder='{"key": "value"}' />
        </Form.Item>
      </Form>
    </Create>
  );
};
